// src/components/phases/VotingPhase.js
import React, { memo, useState } from 'react';
import { SubmittedCard } from '../common/Card';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import './VotingPhase.css';

/**
 * VotingPhase Component
 * Displays the voting phase UI where Judge chooses the winning submission
 */
const VotingPhase = memo(
  ({
    submissions,
    judge,
    currentUsername,
    currentBlackCard,
    onCzarVote,
    rhpDoubleAwardEnabled,
    canUseDoubleAward,
  }) => {
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    // RHP: Judge Double Award — toggle + up-to-2 selection state
    // NOTE: selection is tracked by array INDEX, not playerId — a wagering player's
    // submission is split into 2 separate entries that share the same playerId, and
    // the Judge must be able to select both of them independently (see #229 design notes).
    const [doubleAwardMode, setDoubleAwardMode] = useState(false);
    const [selectedDoubleIndices, setSelectedDoubleIndices] = useState([]);
    const [doubleConfirmOpen, setDoubleConfirmOpen] = useState(false);

    console.log('🗳️ VotingPhase component rendered');
    console.log('🗳️ Current submissions:', submissions);
    console.log('🗳️ Submissions count:', submissions.length);

    const judgeUsername = judge?.username || 'Unknown';
    const isJudge = currentUsername === judgeUsername;

    console.log('🗳️ Judge info - username:', judgeUsername, 'isJudge:', isJudge);
    console.log('🗳️ Current username:', currentUsername);

    const isSelectedForDouble = (index) => selectedDoubleIndices.includes(index);

    const selectedDoubleSubmissions = selectedDoubleIndices
      .map((i) => submissions?.[i])
      .filter(Boolean);

    // Handlers for confirmation modal
    const handleSubmissionClick = (submission, index) => {
      if (doubleAwardMode) {
        setSelectedDoubleIndices((prev) => {
          if (prev.includes(index)) return prev.filter((i) => i !== index);
          if (prev.length >= 2) return prev; // 3rd click is a no-op until one is deselected
          return [...prev, index];
        });
        return;
      }
      setSelectedSubmission(submission);
    };

    const handleConfirm = () => {
      if (selectedSubmission) {
        onCzarVote(selectedSubmission);
        setSelectedSubmission(null);
      }
    };

    const handleCancel = () => {
      setSelectedSubmission(null);
    };

    const handleToggleDoubleAwardMode = () => {
      if (!canUseDoubleAward) return;
      setDoubleAwardMode((prev) => !prev);
      setSelectedDoubleIndices([]);
    };

    const handleConfirmDouble = () => {
      if (selectedDoubleSubmissions.length === 2) {
        onCzarVote(selectedDoubleSubmissions);
        setSelectedDoubleIndices([]);
        setDoubleConfirmOpen(false);
        setDoubleAwardMode(false);
      }
    };

    const handleCancelDoubleConfirm = () => {
      setDoubleConfirmOpen(false);
    };

    if (!isJudge) {
      console.log('🗳️ Not Judge - showing waiting message');
      return (
        <div className="waiting-for-czar">
          <h3>{judgeUsername} is choosing the winner...</h3>
          <p>
            <strong>{judgeUsername}</strong> ⚖️ is reviewing all submissions
          </p>
          <div className="submissions-preview">
            <p>{submissions.length} combinations submitted</p>
          </div>
        </div>
      );
    }

    console.log('🗳️ Is Judge - showing voting interface');

    const doubleAwardTooltip = canUseDoubleAward
      ? 'Once per game, pick 2 winners instead of 1 — each gets the full point (and pot, if there is wagering)!'
      : "You've already used your Double Award this game.";

    return (
      <div className="voting-phase">
        <h2>Choose the Winning Combination!</h2>
        <p className="voting-instructions">
          {doubleAwardMode
            ? `Pick 2 combinations to award — click to select, click again to deselect (${selectedDoubleSubmissions.length}/2 selected)`
            : 'Click on any card combination to select it as the winner!'}
        </p>

        {rhpDoubleAwardEnabled && (
          <div className="double-award-controls">
            <button
              type="button"
              className={`double-award-toggle-button${doubleAwardMode ? ' active' : ''}`}
              onClick={handleToggleDoubleAwardMode}
              disabled={!canUseDoubleAward}
            >
              🎭 {doubleAwardMode ? 'Cancel Double Award' : 'Use Double Award'}
            </button>
            <span className="info-icon" data-tooltip={doubleAwardTooltip}>
              i
            </span>
          </div>
        )}

        <div className="submissions">
          {(submissions || []).map((submission, index) => {
            console.log(`🎨 Rendering submission ${index}:`, {
              playerName: submission?.playerName || 'Unknown',
              playerId: submission?.playerId || 'Unknown',
              cardCount: submission?.cards?.length || 0,
              isBot: submission?.playerName?.includes('Bot') || false,
              cards: submission?.cards || [],
            });

            // SAFETY CHECK: Skip submissions with no cards
            if (
              !submission?.cards ||
              !Array.isArray(submission.cards) ||
              submission.cards.length === 0
            ) {
              console.log(`⚠️ Skipping submission ${index} - no valid cards found`);
              return null;
            }

            const selectedInDoubleMode = doubleAwardMode && isSelectedForDouble(index);

            return (
              <div
                key={submission.id || index}
                className={`submission-option clickable-submission${selectedInDoubleMode ? ' double-award-selected' : ''}`}
                onClick={() => handleSubmissionClick(submission, index)}
                title={
                  doubleAwardMode
                    ? 'Click to select/deselect this combination as one of the 2 winners!'
                    : 'Click to choose this combination as the winner!'
                }
              >
                <div className="submission-cards">
                  {(submission.cards || []).map((card, cardIndex) => (
                    <SubmittedCard
                      key={cardIndex}
                      text={typeof card === 'string' ? card : card?.text || 'Invalid card'}
                    />
                  ))}
                </div>
                <div className="hover-indicator">
                  <span className="choose-text">
                    {selectedInDoubleMode
                      ? '✅ Selected'
                      : doubleAwardMode
                        ? '⭐ Pick as Winner'
                        : '🏆 Choose This!'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {doubleAwardMode && (
          <div className="double-award-bar">
            <Button
              variant="primary"
              disabled={selectedDoubleSubmissions.length !== 2}
              onClick={() => setDoubleConfirmOpen(true)}
            >
              🏆 Confirm 2 Winners ({selectedDoubleSubmissions.length}/2)
            </Button>
          </div>
        )}

        {/* Confirmation Modal — single winner */}
        <Modal
          show={!!selectedSubmission}
          onClose={handleCancel}
          title="Confirm Winner Selection"
          footer={
            <>
              <Button variant="secondary" onClick={handleCancel}>
                CANCEL
              </Button>
              <Button variant="primary" onClick={handleConfirm}>
                🏆 CONFIRM
              </Button>
            </>
          }
          size="md"
          contentClassName="confirmation-modal"
        >
          <p className="modal-instruction">
            Are you sure you want to choose this combination as the winner?
          </p>
          <div className="modal-cards">
            {(selectedSubmission?.cards || []).map((card, cardIndex) => (
              <SubmittedCard
                key={cardIndex}
                text={typeof card === 'string' ? card : card?.text || 'Invalid card'}
              />
            ))}
          </div>
        </Modal>

        {/* Confirmation Modal — Double Award (2 winners) */}
        <Modal
          show={doubleConfirmOpen}
          onClose={handleCancelDoubleConfirm}
          title="Confirm Double Award — 2 Winners"
          footer={
            <>
              <Button variant="secondary" onClick={handleCancelDoubleConfirm}>
                CANCEL
              </Button>
              <Button variant="primary" onClick={handleConfirmDouble}>
                🏆 CONFIRM
              </Button>
            </>
          }
          size="md"
          contentClassName="confirmation-modal"
        >
          <p className="modal-instruction">
            Award victory to both of these combinations? Each winner gets the full point (and full
            pot, if there is wagering)!
          </p>
          {selectedDoubleSubmissions.map((submission, i) => (
            <div className="modal-cards" key={submission.id || i}>
              {(submission.cards || []).map((card, cardIndex) => (
                <SubmittedCard
                  key={cardIndex}
                  text={typeof card === 'string' ? card : card?.text || 'Invalid card'}
                />
              ))}
            </div>
          ))}
        </Modal>
      </div>
    );
  }
);

export default VotingPhase;
